import { 
  JournalEntry, 
  FiscalCategory,
  LineItem 
} from '../types/journalEntry';
import { 
  FundType, 
  RestrictionType,
  AccountCategory,
  CategoryHierarchy 
} from '../types/accounting';

export interface ValidationResult {
  errors: string[];
  warnings: string[];
  isValid: boolean;
}

// Validation Functions
export const validateEntry = (entry: Partial<JournalEntry>): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!entry.date) errors.push('Date is required');
  if (!entry.description?.trim()) errors.push('Description is required');
  if (!entry.accountCategory) errors.push('Account category is required');
  if (!entry.accountCode) errors.push('Account code is required');
  
  // Validate fiscal category if present
  const fiscalCategory = entry.fiscalCategory as FiscalCategory | undefined;
  if (fiscalCategory) {
    if (fiscalCategory.fundType && !fiscalCategory.restrictionType) {
      errors.push('Restriction type is required when fund type is specified');
    }
  }

  // Validate line items
  if (!entry.lineItems?.length) {
    errors.push('At least one line item is required');
  } else {
    const lineItemErrors = validateLineItems(entry.lineItems);
    errors.push(...lineItemErrors);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

export const validateBalance = (lineItems: LineItem[]): boolean => {
  const total = lineItems.reduce((sum, item) => sum + item.amount, 0);
  return Math.abs(total) < 0.01; // Using 0.01 to handle floating point precision
};

export const validateLineItems = (lineItems: LineItem[]): string[] => {
  const errors: string[] = [];

  lineItems.forEach((item, index) => {
    if (!item.accountId) {
      errors.push(`Line item ${index + 1}: Account is required`);
    }
    if (!item.description?.trim()) {
      errors.push(`Line item ${index + 1}: Description is required`);
    }
    if (typeof item.amount !== 'number' || isNaN(item.amount)) {
      errors.push(`Line item ${index + 1}: Valid amount is required`);
    }
  });

  return errors;
};

// Smart Suggestions
interface Suggestion {
  accountId: string;
  description: string;
  relevanceScore: number;
}

export const updateWithSuggestions = async (
  description: string,
  amount: number,
  historicalEntries: JournalEntry[]
): Promise<Suggestion[]> => {
  const suggestions: Suggestion[] = [];

  // Find similar historical entries
  historicalEntries.forEach(entry => {
    entry.lineItems?.forEach(item => {
      const relevanceScore = calculateRelevanceScore(item.accountId, description, amount);
      if (relevanceScore > 0.5) { // Threshold for relevance
        suggestions.push({
          accountId: item.accountId || '',
          description: item.description || '',
          relevanceScore
        });
      }
    });
  });

  return suggestions.sort((a, b) => b.relevanceScore - a.relevanceScore).slice(0, 5);
};

export const calculateRelevanceScore = (
  accountId: string,
  description: string,
  amount: number
): number => {
  // Implement fuzzy matching and similarity scoring
  // This is a simplified example - you might want to use a more sophisticated algorithm
  return Math.random(); // Placeholder implementation
};

// Category Helpers
export const getCategoryHierarchy = async (categoryId: string): Promise<CategoryHierarchy> => {
  throw new Error('getCategoryHierarchy must be implemented');
};

export const getAccountsForCategory = async (
  categoryId: string,
  subcategoryId?: string
): Promise<AccountCategory[]> => {
  throw new Error('getAccountsForCategory must be implemented');
};

// Helper function to format currency amounts
export const formatAmount = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

// Helper function to generate a unique ID for new line items
export const generateLineItemId = (): string => {
  return crypto.randomUUID();
}; 